import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../../model/SketchEntityReferenceDiagnostics';
import { isRetainedSketchEntityReferenceRecord } from '../../model/SketchEntityReferenceRecord';
import type {
	SketchEntityReferenceRepository,
	SketchEntityReferenceRepositoryWriteScope,
} from '../../repository/SketchEntityReferenceRepository';
import type {
	SketchEntityReferenceCycleGuardInput,
	SketchEntityReferenceCycleGuardResult,
	EntityGraphNode,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceCycleGuard';

export type {
	SketchEntityReferenceCycleGuardInput,
	SketchEntityReferenceCycleGuardResult,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceCycleGuard';

/**
 * 草图实体引用循环检测器。
 *
 * 设计说明：
 * 1. Convert Entities 的依赖方向是 source entity -> target entity；
 * 2. 新增边之前从新 target 出发查找是否能回到 source，即可判断是否形成环；
 * 3. 这里只依赖仓储查询能力，保持检测逻辑脱离 UI 和运行时场景对象。
 */
export class SketchEntityReferenceCycleGuard {
	/**
	 * @param dependencies 循环检测依赖。
	 */
	public constructor(
		private readonly dependencies: {
			/** 引用仓储。 */
			repository: SketchEntityReferenceRepository;
		},
	) {}

	/**
	 * 检查新增引用是否会形成循环。
	 *
	 * @param input 检查输入。
	 * @returns 循环检测结果。
	 */
	public async check(
		input: SketchEntityReferenceCycleGuardInput,
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
	): Promise<SketchEntityReferenceCycleGuardResult> {
		const sourceKey = this.buildEntityKey(input.sourceSketchId, input.sourceEntityId);
		const targetKey = this.buildEntityKey(input.targetSketchId, input.targetEntityId);
		const targetCanReachSource = await this.canReach({
			fromNode: {
				sketchId: input.targetSketchId,
				entityId: input.targetEntityId,
			},
			targetNode: {
				sketchId: input.sourceSketchId,
				entityId: input.sourceEntityId,
			},
			visitedEntityKeys: new Set<string>(),
			repository,
		});

		if (!targetCanReachSource) {
			return { allowed: true };
		}

		return {
			allowed: false,
			diagnostic: createExecutionDiagnostic({
				kind: 'input-invalid',
				level: 'error',
				message: `检测到循环引用，无法创建转换实体引用: ${sourceKey} -> ${targetKey}`,
				sourceRefEntityId: input.sourceEntityId,
				targetEntityId: input.targetEntityId,
			}),
		};
	}

	/**
	 * 判断依赖图中 from 是否可以到达 target。
	 *
	 * @param input 可达性输入。
	 * @returns 可以到达时返回 true。
	 */
	private async canReach(input: {
		fromNode: EntityGraphNode;
		targetNode: EntityGraphNode;
		visitedEntityKeys: Set<string>;
		repository: SketchEntityReferenceRepositoryWriteScope;
	}): Promise<boolean> {
		const fromEntityKey = this.buildEntityKey(input.fromNode.sketchId, input.fromNode.entityId);
		const targetEntityKey = this.buildEntityKey(input.targetNode.sketchId, input.targetNode.entityId);
		if (fromEntityKey === targetEntityKey) {
			return true;
		}
		if (input.visitedEntityKeys.has(fromEntityKey)) {
			return false;
		}
		input.visitedEntityKeys.add(fromEntityKey);

		const downstreamReferences = await input.repository.listReferencesBySourceEntity(
			input.fromNode.sketchId,
			input.fromNode.entityId,
		);
		for (const reference of downstreamReferences) {
			if (!isRetainedSketchEntityReferenceRecord(reference)) {
				continue;
			}
			if (await this.canReach({
				fromNode: {
					sketchId: reference.targetSketchId,
					entityId: reference.targetEntityId,
				},
				targetNode: input.targetNode,
				visitedEntityKeys: input.visitedEntityKeys,
				repository: input.repository,
			})) {
				return true;
			}
		}

		return false;
	}

	/**
	 * 构建实体依赖图 key。
	 *
	 * @param sketchId 草图 ID。
	 * @param entityId 实体 ID。
	 * @returns 实体 key。
	 */
	private buildEntityKey(sketchId: string, entityId: string): string {
		return `${sketchId}::${entityId}`;
	}
}
